import {Platform} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Keychain from 'react-native-keychain';

/**
 * Low-level secure key-value store.
 *
 * Credential material and PHI must live in the OS secure store (iOS Keychain /
 * Android Keystore), never in AsyncStorage — AsyncStorage is unencrypted
 * plaintext on disk and extractable from any rooted/jailbroken device
 * (engineering-standards react-native [HARD] rule).
 *
 * Each value gets its own Keychain service so entries can be read, rotated,
 * and cleared independently. Web has no secure store, so it falls back to
 * AsyncStorage under the caller's legacy key — behavior there is unchanged.
 *
 * This module deliberately depends only on the storage SDKs (not
 * GeneralStorage) so higher-level stores can build on it without import
 * cycles.
 */

export const SECURE_SERVICES = {
  accessToken: 'com.diathrive.accessToken',
  userProfile: 'com.diathrive.userProfile',
  refreshToken: 'com.diathrive.refreshToken',
};

// AFTER_FIRST_UNLOCK: background BLE AutoSend uploads glucose readings while
// the phone is locked, so the token/profile must be readable then (still
// encrypted at rest until the first unlock after boot). THIS_DEVICE_ONLY:
// credential material never migrates to another device via backup/restore —
// a fresh device gets a fresh login.
const keychainOptions = service => ({
  service,
  accessible: Keychain.ACCESSIBLE.AFTER_FIRST_UNLOCK_THIS_DEVICE_ONLY,
});

// The API layer reads the access token on every request; a process-lifetime
// cache keeps that off the Keychain hot path (standards: access token may
// live in memory). Writes/removes keep it coherent.
const memoryCache = new Map();

// Failures here must be visible in release builds, where console.* is
// stripped. Logger is required lazily to avoid a module cycle at import time
// and never receives values — only the service name and error code.
const reportFailure = (operation, service, e) => {
  if (__DEV__) {
    // eslint-disable-next-line no-console
    console.warn(`${operation} failed for ${service}:`, e?.message);
    return;
  }
  try {
    const {logger} = require('./Logger');
    logger.warn(`secure-store ${operation} failed`, {
      service,
      code: e?.code,
      message: e?.message,
    });
  } catch (_ignored) {
    // Logging must never break a storage operation.
  }
};

// AsyncStorage is wiped on uninstall; the OS Keychain is NOT. Without this
// guard, a deleted app would leave the session token and patient profile
// behind on the device (and hand them to a reinstall). The marker lives in
// AsyncStorage precisely because it dies with the install.
//
// Ordering matters: context providers start reading the profile during the
// FIRST render, before any boot effect runs, and the lazy migration deletes
// the plaintext copy as it moves values into the Keychain. If the purge ran
// concurrently it could wipe a just-migrated value forever. So every store
// operation awaits the one-time provisioning check — the first caller (in
// program order) runs it, everyone else queues behind the same promise.
const PROVISIONED_MARKER = 'secure_store_provisioned';
let provisioningPromise = null;

const runFreshInstallCheck = async () => {
  try {
    const marker = await AsyncStorage.getItem(PROVISIONED_MARKER);
    if (marker) {
      return;
    }
    await Promise.all(
      Object.values(SECURE_SERVICES).map(async service => {
        memoryCache.delete(service);
        try {
          await Keychain.resetGenericPassword({service});
        } catch (e) {
          reportFailure('fresh-install purge', service, e);
        }
      }),
    );
    await AsyncStorage.setItem(PROVISIONED_MARKER, 'true');
  } catch (e) {
    reportFailure('fresh-install check', 'all', e);
  }
};

const ensureProvisioned = () => {
  if (Platform.OS === 'web') {
    return Promise.resolve();
  }
  if (!provisioningPromise) {
    provisioningPromise = runFreshInstallCheck();
  }
  return provisioningPromise;
};

/** Eagerly kick the fresh-install check at app boot (see App.js). Every
 * store operation also awaits it, so calling this is an optimization, not
 * a correctness requirement. */
export const purgeSecureStoreOnFreshInstall = () => ensureProvisioned();

/**
 * Store a string value under a Keychain service (web: AsyncStorage legacy key).
 * Also purges any plaintext copy left under the legacy AsyncStorage key from
 * builds that predate the secure store.
 */
export const secureSet = async (service, legacyKey, value) => {
  if (value === null || value === undefined) {
    return;
  }
  if (Platform.OS === 'web') {
    await AsyncStorage.setItem(legacyKey, String(value));
    return;
  }
  await ensureProvisioned();
  try {
    await Keychain.setGenericPassword(service, String(value), keychainOptions(service));
    memoryCache.set(service, String(value));
    if (legacyKey) {
      await AsyncStorage.removeItem(legacyKey);
    }
  } catch (e) {
    // Never log the value — it may be a token or PHI.
    reportFailure('secureSet', service, e);
  }
};

/**
 * Read a value. If the secure store is empty but a plaintext copy exists under
 * the legacy AsyncStorage key (pre-migration installs), migrate it: write it
 * to the Keychain, delete the plaintext copy, and return it.
 */
export const secureGet = async (service, legacyKey) => {
  if (Platform.OS === 'web') {
    return AsyncStorage.getItem(legacyKey);
  }
  await ensureProvisioned();
  if (memoryCache.has(service)) {
    return memoryCache.get(service);
  }
  let keychainFailed = false;
  try {
    const credentials = await Keychain.getGenericPassword(keychainOptions(service));
    if (credentials && credentials.password) {
      memoryCache.set(service, credentials.password);
      return credentials.password;
    }
  } catch (e) {
    keychainFailed = true;
    reportFailure('secureGet', service, e);
  }
  if (!legacyKey) {
    return null;
  }
  const legacyValue = await AsyncStorage.getItem(legacyKey);
  if (legacyValue !== null && legacyValue !== undefined) {
    if (keychainFailed) {
      // The Keychain is unreadable on this device right now. Serve the
      // plaintext copy but do NOT migrate — migrating would delete the only
      // retrievable copy behind a store we cannot read back.
      return legacyValue;
    }
    await secureSet(service, legacyKey, legacyValue);
    return legacyValue;
  }
  return null;
};

/** Remove a value from the secure store and any legacy plaintext copy. */
export const secureRemove = async (service, legacyKey) => {
  if (Platform.OS !== 'web') {
    await ensureProvisioned();
  }
  memoryCache.delete(service);
  if (Platform.OS !== 'web') {
    try {
      await Keychain.resetGenericPassword({service});
    } catch (e) {
      reportFailure('secureRemove', service, e);
    }
  }
  if (legacyKey) {
    await AsyncStorage.removeItem(legacyKey);
  }
};

/** Test-only: reset the process cache + provisioning gate for a cold store. */
export const __clearSecureStoreCacheForTests = () => {
  memoryCache.clear();
  provisioningPromise = null;
};
